import { useEffect, useRef, useState, useCallback } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

interface CinematicAudioProps {
  /** Audio src to loop (place file in /public). If missing or fails, runs silently. */
  src?: string;
  /** Whether the reveal phase is active (controls play/pause) */
  active: boolean;
}

/**
 * Optional ambient bed for the cinematic shell.
 * - Default OFF (browser autoplay + accessibility).
 * - Toggle button bottom-right; persists choice in localStorage.
 * - Silently no-ops if src is undefined or load fails.
 */
export function CinematicAudio({ src, active }: CinematicAudioProps) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [available, setAvailable] = useState(Boolean(src));

  // Restore preference
  useEffect(() => {
    try {
      const saved = localStorage.getItem('reveal_audio_muted');
      if (saved === 'false') setMuted(false);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!src || !audioRef.current) return;
    const a = audioRef.current;
    a.volume = 0.35;
    a.loop = true;

    if (active && !muted) {
      a.play().catch(() => setAvailable(false));
    } else {
      a.pause();
    }
  }, [active, muted, src]);

  const toggle = useCallback(() => {
    setMuted((m) => {
      const next = !m;
      try { localStorage.setItem('reveal_audio_muted', String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);

  if (!available) return null;

  return (
    <>
      {src && (
        <audio
          ref={audioRef}
          src={src}
          preload="auto"
          onError={() => setAvailable(false)}
        />
      )}
      <button
        type="button"
        onClick={toggle}
        aria-label={muted ? 'Unmute reveal audio' : 'Mute reveal audio'}
        className="fixed bottom-6 right-4 z-[60] rounded-full backdrop-blur-md bg-black/30 hover:bg-black/50 text-white/80 hover:text-white p-2.5 transition-colors"
      >
        {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>
    </>
  );
}
